import { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'

import { api, LinkedInRepoSnapshot, LinkedInTopMover } from '../../../services/api'
import { healthPalette } from './linkedinStyles'

/**
 * Which repos are moving, and how they compare.
 *
 * `linkedin_repo_snapshots` has been filled every day since the repo agent
 * started and read by almost nothing: a single repo's sparkline lived inside a
 * detail modal, so comparing two repos meant opening two modals and
 * remembering. This puts the whole tracked set on one axis.
 *
 * The honest caveat is built in: one snapshot is a dot, not a trajectory. Until
 * there are at least two days of history the chart says so rather than drawing a
 * flat line that reads as "no growth", and the activity table — which comes from
 * the repo's own commit count, not from our history — carries the view instead.
 */

const METRICS = [
    { key: 'stars', label: 'Estrellas' },
    { key: 'forks', label: 'Forks' },
    { key: 'commit_count_30d', label: 'Commits (30d)' },
] as const

type Metric = (typeof METRICS)[number]['key']

const RANGES = [
    { label: '7 días', days: 7 },
    { label: '30 días', days: 30 },
    { label: '90 días', days: 90 },
]

/**
 * How to plot repos whose magnitudes differ by orders of magnitude.
 *
 * Absolute values on one shared linear axis cannot compare them: with a 224k-star
 * repo setting the scale, a repo climbing 966 → 1487 (+54% — the fastest mover on
 * the board) occupies two tenths of a percent of the plot height and renders as a
 * flat line on the floor. The chart ends up contradicting its own table. Percent
 * change from the start of the window puts every repo on the same axis and
 * answers the question the panel is actually for: who is growing fastest.
 */
const VIEWS = [
    { key: 'relative', label: '% variación' },
    { key: 'absolute', label: 'Valor absoluto' },
] as const

type View = (typeof VIEWS)[number]['key']

// Enough distinct hues for the comparison lines without going neon.
const COLORS = [
    '#1976D2',
    '#E53935',
    '#43A047',
    '#FB8C00',
    '#8E24AA',
    '#00ACC1',
    '#3949AB',
    '#D81B60',
]

const Wrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`

const Panel = styled.div`
  background: #fff;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 1rem 1.1rem;
  border: 1px solid rgba(0, 0, 0, 0.07);
`

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.75rem;
`

const Chip = styled.button<{ $on: boolean }>`
  border: 1px solid ${({ $on }) => ($on ? '#1976D2' : 'rgba(0,0,0,0.15)')};
  background: ${({ $on }) => ($on ? '#E3F2FD' : 'transparent')};
  color: ${({ $on }) => ($on ? '#0D47A1' : 'inherit')};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 0.25rem 0.7rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
`

const Title = styled.h3`
  margin: 0 0 0.25rem;
  font-size: 1rem;
`

const Muted = styled.p`
  opacity: 0.7;
  font-size: 0.85rem;
  margin: 0.25rem 0 0;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;

  th,
  td {
    text-align: left;
    padding: 0.4rem 0.5rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  th {
    font-weight: 600;
    opacity: 0.7;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.05em;
  }

  td.num,
  th.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
`

const Delta = styled.span<{ $up: boolean }>`
  font-weight: 700;
  color: ${({ $up }) => ($up ? '#2E7D32' : '#78909C')};
`

/** The health number, coloured by the same palette the cards and modal use, so
 *  one repo's score never reads as two different verdicts across the app. */
const Health = styled.span<{ $color: string }>`
  font-weight: 700;
  color: ${({ $color }) => $color};
`

const Swatch = styled.span<{ $color: string }>`
  display: inline-block;
  width: 0.65rem;
  height: 0.65rem;
  border-radius: 2px;
  margin-right: 0.45rem;
  background: ${({ $color }) => $color};
`

const RowButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-align: left;
`

/** The first value a repo has inside the window — the baseline % change is measured from. */
function baselineOf(points: LinkedInRepoSnapshot[], metric: Metric): number | null {
    const first = [...points]
        .sort((a, b) => a.snapshot_date.localeCompare(b.snapshot_date))
        .find((p) => p[metric] !== null && p[metric] !== undefined)
    return first ? Number(first[metric]) : null
}

/**
 * Repos that cannot be drawn as a percentage: their baseline is zero, and growth
 * from zero has no percentage. Returned so the caller can say so out loud instead
 * of leaving a silently missing line — a repo that went 0 → 40 commits is the most
 * interesting row on the board, not one to drop without a word.
 */
export function immeasurableInPercent(
    byRepo: Record<number, LinkedInRepoSnapshot[]>,
    metric: Metric,
): number[] {
    return Object.entries(byRepo)
        .filter(([, points]) => !baselineOf(points, metric))
        .map(([id]) => Number(id))
}

/** One row per day, one column per repo, which is what recharts wants. */
export function toSeries(
    byRepo: Record<number, LinkedInRepoSnapshot[]>,
    metric: Metric,
    view: View = 'absolute',
): { day: string; [repo: string]: number | string }[] {
    const days = new Set<string>()
    Object.values(byRepo).forEach((points) => points.forEach((p) => days.add(p.snapshot_date)))

    const baselines: Record<string, number | null> = {}
    if (view === 'relative') {
        Object.entries(byRepo).forEach(([id, points]) => {
            baselines[id] = baselineOf(points, metric)
        })
    }

    return [...days]
        .sort()
        .map((day) => {
            const row: { day: string; [repo: string]: number | string } = { day }
            Object.entries(byRepo).forEach(([id, points]) => {
                const hit = points.find((p) => p.snapshot_date === day)
                const value = hit?.[metric]
                if (value === null || value === undefined) return
                if (view === 'absolute') {
                    row[id] = value
                    return
                }
                const base = baselines[id]
                if (!base) return
                row[id] = ((Number(value) - base) / base) * 100
            })
            return row
        })
}

export const RepoMetrics: React.FC = () => {
    const [metric, setMetric] = useState<Metric>('stars')
    const [days, setDays] = useState(30)
    // Percent by default: this panel exists to compare repos, and absolute
    // values only compare repos that happen to share an order of magnitude.
    const [view, setView] = useState<View>('relative')
    const [movers, setMovers] = useState<LinkedInTopMover[]>([])
    const [selected, setSelected] = useState<number[]>([])
    const [history, setHistory] = useState<Record<number, LinkedInRepoSnapshot[]>>({})
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        let cancelled = false
        setFailed(false)
        api.linkedin
            .getTopMovers(metric, days, 10)
            .then((res) => {
                if (cancelled) return
                const items = res.data.items
                setMovers(items)
                // Preselect the leaders so the chart is useful without a click.
                setSelected(items.slice(0, 4).map((m) => m.id))
            })
            .catch(() => !cancelled && setFailed(true))
        return () => {
            cancelled = true
        }
    }, [metric, days])

    useEffect(() => {
        let cancelled = false
        const missing = selected.filter((id) => !history[id])
        if (!missing.length) return
        Promise.all(
            missing.map((id) =>
                api.linkedin
                    .getRepoTimeseries(id, days)
                    .then((res) => [id, res.data.points] as const)
                    // One repo's history failing must not blank the comparison.
                    .catch(() => [id, [] as LinkedInRepoSnapshot[]] as const),
            ),
        ).then((pairs) => {
            if (cancelled) return
            setHistory((prev) => ({ ...prev, ...Object.fromEntries(pairs) }))
        })
        return () => {
            cancelled = true
        }
    }, [selected, days, history])

    const toggle = useCallback((id: number) => {
        setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
    }, [])

    const nameOf = useCallback(
        (id: number) => {
            const m = movers.find((x) => x.id === id)
            return m ? `${m.owner}/${m.name}` : String(id)
        },
        [movers],
    )

    const chosen = useMemo(
        () =>
            Object.fromEntries(
                selected.filter((id) => history[id]?.length).map((id) => [id, history[id]]),
            ),
        [selected, history],
    )

    const chartRows = useMemo(() => toSeries(chosen, metric, view), [chosen, metric, view])

    // Named, not hidden: a repo whose baseline is zero has no percentage, and a
    // line that simply isn't there reads as "no data" rather than "not expressible".
    const dropped = useMemo(
        () => (view === 'relative' ? immeasurableInPercent(chosen, metric) : []),
        [chosen, metric, view],
    )

    // Two points is the minimum that draws a line rather than a dot.
    const plottable = chartRows.length >= 2

    if (failed) return <Muted>No se pudieron cargar las métricas.</Muted>

    return (
        <Wrap>
            <Panel>
                <Title>Comparativa de repositorios</Title>
                <Controls>
                    {METRICS.map((m) => (
                        <Chip key={m.key} $on={metric === m.key} onClick={() => setMetric(m.key)}>
                            {m.label}
                        </Chip>
                    ))}
                    <span style={{ width: '0.75rem' }} />
                    {RANGES.map((r) => (
                        <Chip key={r.days} $on={days === r.days} onClick={() => setDays(r.days)}>
                            {r.label}
                        </Chip>
                    ))}
                    <span style={{ width: '0.75rem' }} />
                    {VIEWS.map((v) => (
                        <Chip key={v.key} $on={view === v.key} onClick={() => setView(v.key)}>
                            {v.label}
                        </Chip>
                    ))}
                </Controls>

                {plottable ? (
                    <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={chartRows} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                            <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                            {/* 'auto' on both ends, never anchored at zero: a repo
                                that moves from 220.373 to 223.974 is a flat line on
                                a 0-based axis, which is the whole complaint. */}
                            <YAxis
                                tick={{ fontSize: 11 }}
                                width={56}
                                domain={['auto', 'auto']}
                                tickFormatter={(v: number) =>
                                    view === 'relative' ? `${v > 0 ? '+' : ''}${v.toFixed(0)}%` : String(v)
                                }
                            />
                            <Tooltip
                                formatter={(v) =>
                                    view === 'relative' && typeof v === 'number'
                                        ? `${v > 0 ? '+' : ''}${v.toFixed(1)}%`
                                        : String(v)
                                }
                            />
                            <Legend />
                            {selected.map((id, i) => (
                                <Line
                                    key={id}
                                    type="monotone"
                                    dataKey={String(id)}
                                    name={nameOf(id)}
                                    stroke={COLORS[i % COLORS.length]}
                                    strokeWidth={2}
                                    dot={false}
                                    connectNulls
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <Muted>
                        Todavía no hay suficiente histórico para dibujar una curva: se guarda una
                        instantánea al día, y hacen falta al menos dos. La tabla de abajo ya es
                        válida — sale del propio repositorio, no de nuestro histórico.
                    </Muted>
                )}
                {dropped.length > 0 && (
                    <Muted>
                        Sin porcentaje para {dropped.map(nameOf).join(', ')}: partían de cero en
                        este periodo, y un crecimiento desde cero no se puede expresar como
                        variación. Cámbialo a «Valor absoluto» para verlos.
                    </Muted>
                )}
            </Panel>

            <Panel>
                <Title>Más movimiento en {days} días</Title>
                <Muted>Dentro de los repositorios que seguimos. Pulsa una fila para añadirla o quitarla del gráfico.</Muted>
                <Table>
                    <thead>
                        <tr>
                            <th>Repositorio</th>
                            <th>Lenguaje</th>
                            <th className="num">Salud</th>
                            <th className="num">Antes</th>
                            <th className="num">Ahora</th>
                            <th className="num">Variación</th>
                        </tr>
                    </thead>
                    <tbody>
                        {movers.map((m) => {
                            const on = selected.indexOf(m.id)
                            return (
                                <tr key={m.id}>
                                    <td>
                                        <RowButton onClick={() => toggle(m.id)}>
                                            {on >= 0 && <Swatch $color={COLORS[on % COLORS.length]} />}
                                            {m.owner}/{m.name}
                                        </RowButton>
                                    </td>
                                    <td>{m.language || '—'}</td>
                                    <td className="num">
                                        {typeof m.health_score === 'number' ? (
                                            <Health $color={healthPalette(m.health_score).color}>
                                                {m.health_score}
                                            </Health>
                                        ) : (
                                            '—'
                                        )}
                                    </td>
                                    <td className="num">{m.value_from ?? '—'}</td>
                                    <td className="num">{m.value_to ?? '—'}</td>
                                    <td className="num">
                                        <Delta $up={m.delta > 0}>
                                            {m.delta > 0 ? '+' : ''}
                                            {m.delta}
                                        </Delta>
                                    </td>
                                </tr>
                            )
                        })}
                        {!movers.length && (
                            <tr>
                                <td colSpan={6}>
                                    <Muted>Sin datos todavía para este periodo.</Muted>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Panel>
        </Wrap>
    )
}

export default RepoMetrics
